/**
 * Capa de Datos (DL) para la dinámica "Medallas Tradiciones Perronas 2026" (tpx2026 / Tile 52).
 * Conexión directa con la API RESTful de .NET 8 (/api/V1/Xtp).
 */

const rawApiUrl = process.env.REACT_APP_API_URL || '/api/';
const API_URL = rawApiUrl.endsWith('/') ? rawApiUrl : `${rawApiUrl}/`;

/**
 * Obtiene la configuración pública activa de la dinámica XTP (cacheada en el backend).
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const GetPublicConfig = async () => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/config`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return {
                success: false,
                message: err.message || 'No fue posible cargar la configuración de la dinámica.'
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data
        };
    } catch (error) {
        console.error('Error en GetPublicConfig DL:', error);
        return {
            success: false,
            message: 'Error de conexión con el servidor.'
        };
    }
};

/**
 * Registra la participación de un aficionado con validación multicapa y anti-enumeración.
 * @param {object} payload - Datos del participante (Name, Email, Phone, MedalCode, TicketNumber, etc.)
 * @param {string} sessionId - UUID de sesión para rate limiting y trazabilidad defensiva
 * @returns {Promise<object>} Respuesta del servidor (XtpRegisterResultDto o error estándar)
 */
export const RegisterParticipant = async (payload, sessionId) => {
    try {
        const headers = {
            'Content-Type': 'application/json'
        };

        if (sessionId) {
            headers['X-Xtp-Session-Id'] = sessionId;
        }

        const response = await fetch(`${API_URL}V1/Xtp/register`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'No fue posible procesar el registro con los datos ingresados.',
                ...resData
            };
        }

        return resData;
    } catch (error) {
        console.error('Error en RegisterParticipant DL:', error);
        return {
            success: false,
            message: 'Error de conexión con el servidor al registrar la medalla.'
        };
    }
};

/**
 * Valida criptográficamente el token HMAC emitido en el código QR del pase digital.
 * @param {string} token - Token Base64Url del QR
 * @returns {Promise<object>} Resultado de la verificación
 */
export const VerifyToken = async (token) => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/verify-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token })
        });

        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                isValid: false,
                errorMessage: resData.errorMessage || 'Token de verificación no válido o adulterado.'
            };
        }

        return resData;
    } catch (error) {
        console.error('Error en VerifyToken DL:', error);
        return {
            isValid: false,
            errorMessage: 'Error de conexión al verificar el token criptográfico.'
        };
    }
};

/**
 * Registra eventos de telemetría de embudo (visita, inicio de registro, descarga de pase, etc.).
 * Implementa try/catch silencioso para nunca romper la experiencia del aficionado si falla la red.
 * @param {string} eventName - Nombre del evento (ej. 'page_view', 'form_submit', 'box_open', 'pass_download')
 * @param {string} source - Origen del tráfico (ej. 'xolo_shop_local', 'xolo_shop_estadio', 'medalla_qr_direct')
 * @param {string} sessionId - Identificador de sesión
 * @returns {Promise<{ success: boolean }>}
 */
export const TrackMetric = async (eventName, source = 'xolo_shop_local', sessionId = null) => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/metric`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                eventName,
                source,
                sessionId
            })
        });

        if (!response.ok) return { success: false };
        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data };
    } catch (error) {
        // Silencioso por resiliencia de UX
        return { success: false };
    }
};

/**
 * Registra una auditoría de captura de pantalla o descarga del pase digital en el servidor.
 * Implementa try/catch silencioso para no degradar el flujo del usuario.
 * @param {number|null} participantId - ID del aficionado si ya fue registrado
 * @param {string|null} passCode - Folio del pase (#TPX26-XXXXX)
 * @param {string|null} name - Nombre del aficionado
 * @param {string} method - Método de guardado (BUTTON_DOWNLOAD, KEYBOARD_SHORTCUT, etc.)
 * @returns {Promise<{ success: boolean }>}
 */
export const LogScreenshot = async (participantId = null, passCode = null, name = null, method = 'BUTTON_DOWNLOAD') => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/screenshot`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                participantId,
                passCode,
                name,
                method
            })
        });

        if (!response.ok) return { success: false };
        return { success: true };
    } catch (error) {
        // Silencioso por resiliencia de UX
        return { success: false };
    }
};

/**
 * Consulta y recupera pases previamente emitidos por correo, teléfono o folio.
 * @param {string} query - Correo, teléfono (10 dígitos) o folio (#TPX26-XXXXX)
 * @param {string} sessionId - UUID de sesión
 * @returns {Promise<object>} Respuesta del servidor con los pases encontrados
 */
export const RecoverPass = async (query, sessionId = null) => {
    try {
        const headers = {
            'Content-Type': 'application/json'
        };

        if (sessionId) {
            headers['X-Xtp-Session-Id'] = sessionId;
        }

        const response = await fetch(`${API_URL}V1/Xtp/recover-pass`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                query: query?.trim() || '',
                sessionId
            })
        });

        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: resData.message || 'No fue posible recuperar los pases con los datos proporcionados.',
                ...resData
            };
        }

        return resData;
    } catch (error) {
        console.error('Error en RecoverPass DL:', error);
        return {
            success: false,
            message: 'Error de conexión al consultar tus pases.'
        };
    }
};

// ==========================================
// MÉTODOS DEL PANEL ADMINISTRATIVO (HU-05)
// ==========================================

/**
 * Obtiene métricas agregadas del embudo de conversión para el tablero admin.
 */
export const GetAdminMetrics = async () => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/metrics`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Error al consultar métricas administrativas.' };
        }
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('Error en GetAdminMetrics DL:', error);
        return { success: false, message: 'Error de conexión al obtener métricas.' };
    }
};

/**
 * Consulta el padrón paginado y filtrado de participantes registrados.
 */
export const GetAdminParticipants = async (page = 1, pageSize = 25, search = '', status = 'all', sortBy = 'createdAtUtc', sortOrder = 'desc') => {
    try {
        const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize)
        });
        if (search) params.append('search', search);
        if (status && status !== 'all') params.append('status', status);
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);

        const response = await fetch(`${API_URL}V1/Xtp/admin/participants?${params.toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Error al consultar participantes.' };
        }
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('Error en GetAdminParticipants DL:', error);
        return { success: false, message: 'Error de conexión al obtener participantes.' };
    }
};

/**
 * Obtiene la configuración activa de campaña desde la perspectiva administrativa.
 */
export const GetAdminConfig = async () => {
    return await GetPublicConfig();
};

/**
 * Actualiza la configuración de la dinámica en caliente e invalida la caché RAM.
 */
export const UpdateAdminConfig = async (configDto) => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/config`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(configDto)
        });
        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: resData.message || 'Error al actualizar configuración.' };
        }
        return { success: true, data: resData };
    } catch (error) {
        console.error('Error en UpdateAdminConfig DL:', error);
        return { success: false, message: 'Error de conexión al actualizar la configuración.' };
    }
};

/**
 * Obtiene el dataset consolidado para generar el reporte Excel de 2 bloques.
 */
export const GetExportDataset = async () => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/export-dataset`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Error al obtener dataset de exportación.' };
        }
        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error('Error en GetExportDataset DL:', error);
        return { success: false, message: 'Error de conexión al obtener dataset de exportación.' };
    }
};

/**
 * Descalifica a un participante e invalida su pase en tiempo real.
 */
export const DisqualifyParticipant = async (participantId, reason, operatorName = 'Staff Admin') => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/disqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId, reason, operatorName })
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en DisqualifyParticipant DL:', error);
        return { success: false, message: 'Error de conexión al descalificar.' };
    }
};

/**
 * Restituye a un participante previamente descalificado.
 */
export const UndisqualifyParticipant = async (participantId, operatorName = 'Staff Admin') => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/undisqualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ participantId, operatorName })
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en UndisqualifyParticipant DL:', error);
        return { success: false, message: 'Error de conexión al restituir.' };
    }
};

/**
 * Desbloquea manualmente un ticket suspendido por rate limit.
 */
export const UnblockTicket = async (ticketNumber, reason = 'Desbloqueo manual staff') => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/unblock-ticket`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: ticketNumber, reason })
        });
        return await response.json();
    } catch (error) {
        console.error('Error en UnblockTicket DL:', error);
        return { success: false, message: 'Error de conexión al desbloquear ticket.' };
    }
};

/**
 * Desbloquea manualmente una sesión o dispositivo suspendido por rate limit.
 */
export const UnblockSession = async (sessionId, reason = 'Desbloqueo manual staff') => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/unblock-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: sessionId, reason })
        });
        return await response.json();
    } catch (error) {
        console.error('Error en UnblockSession DL:', error);
        return { success: false, message: 'Error de conexión al desbloquear sesión.' };
    }
};

/**
 * Valida criptográficamente y canjea el beneficio físico en tienda o estadio (HU-06).
 * @param {Object} payload - { token, passCode, medalCode, operatorName, source }
 * @returns {Promise<{ success: boolean, status: string, message: string, participant?: Object }>}
 */
export const RedeemParticipant = async (payload) => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/redeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en RedeemParticipant DL:', error);
        return {
            success: false,
            status: 'ERROR',
            message: 'Error de conexión al procesar el canje con el servidor.'
        };
    }
};

/**
 * Revierte el canje de un participante específico regresándolo a estado Pendiente (HU-06).
 * Admite objeto { participantId, passCode, operatorName } o argumentos posicionales (id, operator).
 * @param {Object|number|string} payload - Objeto o ID/PassCode
 * @param {string} [maybeOperator] - Nombre del operador si se usa forma posicional
 * @returns {Promise<{ success: boolean, message: string, affectedCount?: number }>}
 */
export const UnredeemParticipant = async (payload, maybeOperator) => {
    try {
        let bodyPayload = {};
        if (typeof payload === 'number') {
            bodyPayload = { participantId: payload, operatorName: maybeOperator || 'Staff Admin' };
        } else if (typeof payload === 'string') {
            bodyPayload = { passCode: payload, operatorName: maybeOperator || 'Staff Admin' };
        } else if (payload && typeof payload === 'object') {
            bodyPayload = payload;
        }

        const response = await fetch(`${API_URL}V1/Xtp/admin/unredeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bodyPayload)
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en UnredeemParticipant DL:', error);
        return {
            success: false,
            message: 'Error de conexión al intentar revertir el canje.'
        };
    }
};

/**
 * Reinicia masivamente todos los canjes realizados a estado Pendiente previa validación de contraseña (HU-06).
 * @param {Object} payload - { password: string, operatorUser?: string }
 * @returns {Promise<{ success: boolean, message: string, affectedCount?: number }>}
 */
export const ResetAllRedeemed = async (payload) => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/reset-redeemed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en ResetAllRedeemed DL:', error);
        return {
            success: false,
            message: 'Error de conexión al intentar reiniciar los canjes.'
        };
    }
};

/**
 * Precarga o actualiza bajo demanda un lote de medallas en base de datos (HU-08).
 * @param {Object} payload - { prefix, start, end, padDigits, batchName, winnerCodes }
 * @returns {Promise<{ success: boolean, message: string, totalProcessed?: number }>}
 */
export const SeedMedals = async (payload = {}) => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/seed-medals`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en SeedMedals DL:', error);
        return {
            success: false,
            message: 'Error de conexión al sincronizar el lote de medallas con el servidor.'
        };
    }
};

/**
 * Reinicia la dinámica y purga participantes y telemetría previa validación de contraseña maestra (HU-06).
 * @param {string} password - Contraseña maestra de seguridad
 * @param {string} operatorUser - Nombre o identificador del operador
 * @returns {Promise<{ success: boolean, message: string, deletedParticipantsCount?: number }>}
 */
export const ResetDynamicData = async (password, operatorUser = 'admin') => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password, operatorUser })
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en ResetDynamicData DL:', error);
        return {
            success: false,
            message: 'Error de conexión al intentar reiniciar la dinámica.'
        };
    }
};

/**
 * Reinicia exclusivamente las estadísticas y registros de búsqueda del portal /passtpx (HU-06).
 * @param {string} password - Contraseña maestra de seguridad
 * @param {string} operatorUser - Nombre o identificador del operador
 * @returns {Promise<{ success: boolean, message: string, deletedParticipantsCount?: number }>}
 */
export const ResetPassTpxMetrics = async (password, operatorUser = 'admin') => {
    try {
        const response = await fetch(`${API_URL}V1/Xtp/admin/reset-passtpx-metrics`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password, operatorUser })
        });
        const resData = await response.json().catch(() => ({}));
        return resData;
    } catch (error) {
        console.error('Error en ResetPassTpxMetrics DL:', error);
        return {
            success: false,
            message: 'Error de conexión al intentar reiniciar las estadísticas de /passtpx.'
        };
    }
};





